/* ===========================================================================
   c-lp-products-catalog.js — Genuine Parts LP · SHOW PRODUCTS tabs
   ===========================================================================

   Progressive enhancement only. Server-side every category pill is a real
   <a href="/collections/<handle>"> and the first one's products are already
   in the grid, so with JavaScript blocked (BVA's Linux cloak, AGENTS.md) the
   section is complete and every tab navigates to its collection.

   With JavaScript the pill strip becomes an ARIA tablist:
     - role="tablist" / "tab" / "tabpanel", aria-selected, aria-controls,
       roving tabindex, ArrowLeft / ArrowRight / Home / End (automatic
       activation), Space and Enter activate
     - a plain click no longer navigates: the grid for that collection is
       fetched through the Section Rendering API
         /collections/<handle>?section_id=c-lp-products-catalog-grid
       parsed with DOMParser, and ONLY the grid's cards are replaced, trimmed
       to the section's "Products shown" count (data-count)
     - while the request is in flight the panel is aria-busy (dimmed in CSS)
     - responses are cached per collection handle; the server-rendered tab
       seeds the cache, so going back to it never refetches
     - a failed request falls back to the link's own navigation
     - modified clicks (Cmd/Ctrl/Shift/middle) keep the browser's behaviour

   Swapped-in cards are rendered by the same snippet as the server grid and
   carry the same `form[data-atc]`, so assets/c-lp-cart.js (delegated on
   document) adds them to the cart with no re-binding.

   Scoped to each [data-sl-id="c-lp-products-catalog"] root, guarded against
   double binding, re-initialised on shopify:section:load.
   =========================================================================== */
(function () {
  'use strict';

  var ROOT = '[data-sl-id="c-lp-products-catalog"]';
  var ACTIVE = 'c-lp-products-catalog__tab--active';

  function init(root) {
    if (!root || root.hasAttribute('data-catalog-ready')) return;
    if (!window.fetch || !window.DOMParser) return;

    var list = root.querySelector('[data-catalog-tabs]');
    var panel = root.querySelector('[data-catalog-panel]');
    var grid = panel && panel.querySelector('[data-catalog-grid]');
    if (!list || !panel || !grid) return;

    var tabs = Array.prototype.slice.call(list.querySelectorAll('[data-catalog-tab]'));
    if (!tabs.length) return;
    root.setAttribute('data-catalog-ready', '');

    var sectionId = root.getAttribute('data-grid-section') || 'c-lp-products-catalog-grid';
    var count = parseInt(root.getAttribute('data-count'), 10) || 4;
    var copy = {
      atc: root.getAttribute('data-atc-text') || '',
      score: root.getAttribute('data-review-score') || '',
      reviews: root.getAttribute('data-review-count') || '',
      label: root.getAttribute('data-review-label') || '',
      badge: root.getAttribute('data-badge') || ''
    };
    var cache = Object.create(null); // handle -> Promise<Element> (the parsed grid)
    var token = 0;
    var index = 0;

    /* --- ARIA wiring -------------------------------------------------------- */
    list.setAttribute('role', 'tablist');
    panel.setAttribute('role', 'tabpanel');
    tabs.forEach(function (tab, i) {
      if (tab.classList.contains(ACTIVE)) index = i;
      if (tab.parentElement !== list) tab.parentElement.setAttribute('role', 'presentation');
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.removeAttribute('aria-current');
    });
    var current = tabs[index].getAttribute('data-handle');
    cache[current] = Promise.resolve(grid.cloneNode(true));
    mark(index);
    root.classList.add('is-js-ready');

    function mark(i) {
      tabs.forEach(function (tab, j) {
        var on = i === j;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
        tab.classList.toggle(ACTIVE, on);
      });
      panel.setAttribute('aria-labelledby', tabs[i].id);
      index = i;
    }

    /* --- Section Rendering API --------------------------------------------- */
    function request(tab, handle) {
      if (!cache[handle]) {
        var url = tab.pathname || '/collections/' + handle;
        url += (url.indexOf('?') !== -1 ? '&' : '?') + 'section_id=' + sectionId;
        cache[handle] = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then(function (html) {
            var source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-catalog-grid]');
            if (!source) throw new Error('grid missing from section response');
            return source;
          });
        cache[handle].catch(function () { delete cache[handle]; });
      }
      return cache[handle];
    }

    // The fragment renders with the helper section's default copy; re-apply
    // this section's own settings so both grids read the same.
    function applyCopy(card) {
      var button = card.querySelector('form[data-atc] [type="submit"]');
      if (button && !button.disabled && copy.atc) button.textContent = copy.atc;

      var rating = card.querySelector('.c-pdp-prod__rating');
      if (rating && !copy.score) rating.parentNode.removeChild(rating);
      var small = rating && copy.score && rating.querySelector('small');
      if (small) small.textContent = copy.score + ' (' + copy.reviews + ' ' + copy.label + ')';

      var oldBadge = card.querySelector('.c-home-badge');
      if (oldBadge) oldBadge.parentNode.removeChild(oldBadge);
      var tile = card.querySelector('.c-pdp-prod__tile');
      if (copy.badge && tile) {
        var badge = document.createElement('span');
        badge.className = 'c-home-badge';
        badge.textContent = copy.badge;
        tile.insertBefore(badge, tile.firstChild);
      }
    }

    function swap(source) {
      var fragment = document.createDocumentFragment();
      var cards = source.children;
      for (var i = 0; i < cards.length && i < count; i++) {
        var card = document.importNode(cards[i], true);
        applyCopy(card);
        fragment.appendChild(card);
      }
      while (grid.firstChild) grid.removeChild(grid.firstChild);
      grid.appendChild(fragment);
    }

    function activate(i, moveFocus) {
      var tab = tabs[i];
      if (!tab) return;
      mark(i);
      if (moveFocus) tab.focus();

      var handle = tab.getAttribute('data-handle');
      if (handle === current) {
        token++; // a newer choice cancels any request still in flight
        panel.removeAttribute('aria-busy');
        return;
      }

      var mine = ++token;
      panel.setAttribute('aria-busy', 'true');
      request(tab, handle)
        .then(function (source) {
          if (mine !== token) return;
          swap(source);
          current = handle;
          panel.removeAttribute('aria-busy');
        })
        .catch(function () {
          if (mine !== token) return;
          window.location.assign(tab.href); // fall back to the link itself
        });
    }

    /* --- events ------------------------------------------------------------- */
    list.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-catalog-tab]');
      if (!tab || !list.contains(tab)) return;
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      activate(tabs.indexOf(tab), false);
    });

    list.addEventListener('keydown', function (event) {
      var tab = event.target.closest('[data-catalog-tab]');
      if (!tab || !list.contains(tab)) return;
      var i = tabs.indexOf(tab);
      var next;
      switch (event.key) {
        case 'ArrowRight': next = (i + 1) % tabs.length; break;
        case 'ArrowLeft': next = (i - 1 + tabs.length) % tabs.length; break;
        case 'Home': next = 0; break;
        case 'End': next = tabs.length - 1; break;
        case ' ':
        case 'Spacebar': next = i; break;
        default: return; // Enter fires a native click on the link
      }
      event.preventDefault();
      activate(next, true);
    });

    // Theme editor: selecting a tab block shows its products.
    root.addEventListener('shopify:block:select', function (event) {
      var tab = event.target.querySelector && event.target.querySelector('[data-catalog-tab]');
      if (tab) activate(tabs.indexOf(tab), false);
    });
  }

  function initAll(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(ROOT), init);
  }

  initAll(document);

  if (!window.__cLpCatalogBound) {
    window.__cLpCatalogBound = true;
    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target);
    });
  }
})();
